import dict from 'config/dictionary';
import externalLinks from 'config/externalLinks';
import { path, pathOr } from 'ramda';
import React from 'react';
import { interpolateMany } from './interpolate';
import { brandedDictionary, hugggOperator } from './recipientBranding';

const userFriendlyStatuses = [422, 404, 500];

export const TILLO_VOUCHER_TYPES = ['tillo', 'wegift'];

export const isTilloVoucher = (voucherType) =>
  TILLO_VOUCHER_TYPES.includes(voucherType);

// Machine-readable code voucher-core sends when a supplier (currently MBL
// Solutions / Greggs) has blocked ordering. Mirrors the backend wire contract;
// keep the string identical to service-framework's ErrorCode value.
export const SUPPLIER_BLOCKED_ORDER_DUE_TO_INVOICING =
  'SUPPLIER_BLOCKED_ORDER_DUE_TO_INVOICING';

export const isSupplierError = (error) =>
  error?.code === SUPPLIER_BLOCKED_ORDER_DUE_TO_INVOICING;

const supportLink = (
  <a href={externalLinks.email.support.url}>
    {externalLinks.email.support.label}
  </a>
);

export const fallbackError = (
  itemType = 'item',
  action = 'redeeming',
  operator = hugggOperator,
) => {
  const lines = brandedDictionary(operator).unknownAPIError;
  return lines.map((text, index) => (
    <React.Fragment key={index}>
      {interpolateMany(text, [
        ['[item_type]', <>{itemType}</>],
        ['[action]', <>{action}</>],
        ['[support_email]', supportLink],
      ])}
      {index < lines.length - 1 && (
        <>
          <br />
          <br />
        </>
      )}
    </React.Fragment>
  ));
};

const renderErrorLines = (lines, replacements) =>
  lines.map((text, index) => (
    <React.Fragment key={index}>
      {interpolateMany(text, replacements)}
      {index < lines.length - 1 && (
        <>
          <br />
          <br />
        </>
      )}
    </React.Fragment>
  ));

export const tilloVoucherError = (brandName, operator = hugggOperator) => {
  const possessive = brandName ? `${brandName}'s` : `this brand's`;
  return renderErrorLines(brandedDictionary(operator).tilloVoucherError, [
    ['[brand_name]', <>{possessive}</>],
    ['[support_email]', supportLink],
  ]);
};

export const voucherRedemptionError = (operator = hugggOperator) =>
  renderErrorLines(brandedDictionary(operator).voucherRedemptionError, [
    ['[support_email]', supportLink],
  ]);

/**
 * The generic error for an operator: Huggg keeps "item"/"redeeming"; an
 * operator with its own product noun (e.g. "link") is "claiming" it.
 */
export const genericError = (operator = hugggOperator) =>
  operator.productNoun
    ? fallbackError(operator.productNoun, 'claiming', operator)
    : fallbackError('item', 'redeeming', operator);

export const supplierVoucherError = () =>
  renderErrorLines(dict.voucherRedemptionErrorSupplier, []);

/**
 * @deprecated This will be removed at some point
 * @param {Object} err
 */
export const translateAPIError = (err, operator = hugggOperator) => {
  // Raw API text may name Huggg support; an operator that hides that gets
  // its own generic copy instead.
  if (!operator.exposeApiErrorMessages) return genericError(operator);
  let status = Number(path(['response', 'status'], err));
  let message = userFriendlyStatuses.includes(status)
    ? pathOr(
        path(['response', 'data', 'message'], err),
        ['response', 'data', 'error'],
        err,
      )
    : null;
  return message || fallbackError();
};
